/*
 * Nilvara app layer, behaviour: the axis tabs (a slideshow and the product
 * lists after it as one unit), the phone bottom bar, the draw-in of the bands.
 * Stands down in the theme editor (Shopify.designMode) so every rail shows.
 * Uses only the theme's public parts: the slideshow's select() and
 * slideshow:select, the header drawer summary, the search modal's showDialog()
 * and the cart drawer's toggle().
 */
const doc = document;
const design = Boolean(window.Shopify && window.Shopify.designMode);
if (!design) doc.documentElement.classList.add('nv-js');
const motion = matchMedia('(prefers-reduced-motion: reduce)');
const phone = matchMedia('(max-width: 749px)');
// Letters and numbers of any script, so Arabic titles pair as well as English ones.
const norm = (s) => (s || '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
const rtl = () => doc.documentElement.dir === 'rtl';
const clamp = (n) => Math.min(1, Math.max(0, n));
const HEAD = 'h1,h2,h3,h4,h5,h6';
const ready = (fn) => (doc.readyState === 'loading' ? doc.addEventListener('DOMContentLoaded', fn, { once: true }) : fn());

/** One tab per slide, a product list paired to each by title, in step with the real slideshow. */
class NilvaraAxis extends HTMLElement {
  connectedCallback() {
    if (design || this.bar) return;
    ready(() => customElements.whenDefined('slideshow-component').then(() => requestAnimationFrame(() => this.build())));
  }

  build() {
    const host = this.closest('.shopify-section') || this;
    let sec = null;
    const rails = [];
    for (let n = host.nextElementSibling; n; n = n.nextElementSibling) {
      const list = n.querySelector('[data-testid="product-list"]');
      if (!sec) {
        if (list || !n.querySelector('slideshow-component')) return;
        sec = n;
      } else if (list) rails.push(n);
      else break;
    }
    const show = sec && sec.querySelector('slideshow-component');
    const slides = show ? [...show.querySelectorAll('slideshow-slide')].filter((s) => !s.hasAttribute('hidden')) : [];
    if (slides.length < 2) return;

    const titles = slides.map((s, i) => (s.querySelector(HEAD) || {}).textContent?.trim() || String(i + 1));
    const byTitle = new Map();
    rails.forEach((r) => {
      const h = r.querySelector('.section-resource-list__header ' + HEAD) || r.querySelector(HEAD);
      const key = norm(h && h.textContent);
      if (key && !byTitle.has(key)) byTitle.set(key, r);
      r.dataset.nvRail = '';
    });
    const paired = titles.map((t) => byTitle.get(norm(t)) || null);

    const bar = doc.createElement('div');
    bar.className = 'nv-axis__bar';
    bar.setAttribute('role', 'tablist');
    const name = this.dataset.label || show.getAttribute('aria-label') || sec.querySelector('[aria-label]')?.getAttribute('aria-label');
    if (name) bar.setAttribute('aria-label', name);
    const tabs = titles.map((t, i) => {
      const b = doc.createElement('button');
      b.type = 'button';
      b.className = 'nv-axtab';
      b.setAttribute('role', 'tab');
      b.textContent = t;
      if (paired[i] && paired[i].id) b.setAttribute('aria-controls', paired[i].id);
      b.addEventListener('click', () => this.go(i));
      bar.append(b);
      return b;
    });
    this.bar = bar;
    this.tabs = tabs;
    this.rails = rails;
    this.paired = paired;
    this.show = show;
    this.span = [sec, ...rails];
    bar.addEventListener('keydown', (e) => {
      // Arrow keys follow the reading direction: in right to left, Left moves on.
      const k = { ArrowRight: rtl() ? -1 : 1, ArrowLeft: rtl() ? 1 : -1, Home: -9e9, End: 9e9 }[e.key];
      if (!k) return;
      e.preventDefault();
      const to = Math.min(tabs.length - 1, Math.max(0, tabs.indexOf(doc.activeElement) + k));
      this.go(to);
      tabs[to].focus();
    });
    show.addEventListener('slideshow:select', (e) => {
      if (e.target === show) this.set(e.detail.index);
    });
    // Out of the heading section, or the bar could not stick past it on a phone.
    if (host !== this) host.after(this);
    this.append(bar);
    this.setAttribute('data-ready', '');
    this.set(Number.isInteger(show.current) ? show.current : 0);
    this.watch();
  }

  /** Display state for slide i; never moves the slideshow. */
  set(i) {
    this.tabs.forEach((t, k) => {
      t.setAttribute('aria-selected', String(k === i));
      t.tabIndex = k === i ? 0 : -1;
    });
    this.rails.forEach((r) => {
      r.hidden = r !== this.paired[i];
    });
    this.now = i;
    const t = this.tabs[i];
    // Centre the tab by measuring, which holds in either direction (scrollLeft runs negative in right to left).
    const bar = this.bar.getBoundingClientRect();
    const tab = t.getBoundingClientRect();
    this.bar.scrollBy({ left: tab.left + tab.width / 2 - (bar.left + bar.width / 2), behavior: motion.matches ? 'auto' : 'smooth' });
  }

  go(i) {
    this.set(i);
    if (typeof this.show.select === 'function') this.show.select(i, undefined, { animate: !motion.matches });
  }

  /** On a phone the bar sticks under the header and lets go once the unit is behind. */
  watch() {
    let queued = false;
    const tick = () => {
      queued = false;
      if (!phone.matches) return this.bar.removeAttribute('data-off');
      const end = this.span.filter((n) => !n.hidden).pop();
      const stick = parseFloat(getComputedStyle(doc.documentElement).getPropertyValue('--nv-stick')) || 0;
      this.bar.toggleAttribute('data-off', end.getBoundingClientRect().bottom < stick + this.bar.offsetHeight);
    };
    const queue = () => {
      if (!queued) {
        queued = true;
        requestAnimationFrame(tick);
      }
    };
    addEventListener('scroll', queue, { passive: true });
    addEventListener('resize', queue);
    tick();
  }
}
if (!customElements.get('nilvara-axis')) customElements.define('nilvara-axis', NilvaraAxis);

/** Header height under the stuck tabs, while the sticky header shows. */
function stickyOffset() {
  const head = doc.querySelector('header-component');
  if (!head) return;
  const upd = () => doc.documentElement.style.setProperty('--nv-stick', head.dataset.stickyState === 'active' ? head.offsetHeight - 1 + 'px' : '0px');
  new MutationObserver(upd).observe(head, { attributes: true, attributeFilter: ['data-sticky-state'] });
  upd();
}

/** Phone bottom bar: the theme's own drawers, the live count, the river on its top edge. */
function tabbar() {
  const bar = doc.querySelector('.nv-tabbar');
  if (!bar) return;
  const opens = {
    search: () => {
      const m = doc.getElementById('search-modal');
      return Boolean(m && typeof m.showDialog === 'function' && (m.showDialog(), true));
    },
    cart: () => {
      const d = doc.getElementById('cart-drawer');
      return Boolean(d && typeof d.toggle === 'function' && (d.toggle(), true));
    },
    shop: () => {
      const s = doc.querySelector('header-drawer summary');
      return Boolean(s && (s.click(), true));
    },
  };
  bar.addEventListener('click', (e) => {
    const a = e.target.closest('[data-nv-tab]');
    const open = a && opens[a.dataset.nvTab];
    if (open && !(e.metaKey || e.ctrlKey || e.shiftKey) && open()) e.preventDefault();
  });

  const count = bar.querySelector('.nv-tab__count');
  const src = doc.querySelector('.cart-bubble__text-count');
  if (count && src) {
    const sync = () => {
      const t = src.textContent.trim();
      const n = t === '' ? (src.classList.contains('hidden') ? 0 : 100) : parseInt(t, 10) || 0;
      count.textContent = n > 99 ? '99+' : String(n);
      count.hidden = n < 1 || src.classList.contains('hidden');
    };
    new MutationObserver(sync).observe(src, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    sync();
  }

  // The four lines fill in turn, one per chapter.
  const lines = [...bar.querySelectorAll('.nv-tabbar__river i')];
  const marks = () => ['curation', 'delight', 'legacy'].map((k) => doc.querySelector('[data-nv-chapter="' + k + '"]'));
  let queued = false;
  const draw = () => {
    queued = false;
    if (!phone.matches) return;
    const H = doc.documentElement.scrollHeight;
    const room = Math.max(1, H - innerHeight);
    const t = clamp(scrollY / room);
    const y = scrollY + innerHeight * t;
    const found = marks();
    const starts = found.every(Boolean) ? [0, ...found.map((m) => m.getBoundingClientRect().top + scrollY)] : [0, 0.25, 0.5, 0.75].map((f) => f * H);
    lines.forEach((l, i) => {
      const a = starts[i];
      const b = i < 3 ? starts[i + 1] : H;
      l.style.setProperty('--p', clamp((y - a) / Math.max(1, b - a)).toFixed(3));
    });
  };
  const queue = () => {
    if (!queued) {
      queued = true;
      requestAnimationFrame(draw);
    }
  };
  addEventListener('scroll', queue, { passive: true });
  addEventListener('resize', queue);
  addEventListener('load', queue);
  draw();
}

/** Bands and the delta draw in once on entering the viewport. */
function drawIn() {
  const targets = [...doc.querySelectorAll('.nv-wave, .nv-delta')];
  if (!('IntersectionObserver' in window)) return targets.forEach((t) => t.classList.add('is-in'));
  const io = new IntersectionObserver(
    (entries) =>
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      }),
    { threshold: 0.35 }
  );
  targets.forEach((t) => io.observe(t));
}

/**
 * A phone number written in the middle of a right to left sentence is reordered by the bidi
 * algorithm ("+20 121 205 0001" is read as "0001 205 121 20+"). Where the number sits in plain
 * text, such as the footer's contact line, it is wrapped in a left to right isolate.
 */
function isolatePhones() {
  if (!rtl()) return;
  const number = /\+\d[\d ()-]{7,}\d/g;
  const skip = new Set(['SCRIPT', 'STYLE', 'TEXTAREA', 'BDI', 'A']);
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const hits = [];
  while (walker.nextNode()) {
    const n = walker.currentNode;
    if (!skip.has(n.parentNode.nodeName) && number.test(n.nodeValue)) hits.push(n);
    number.lastIndex = 0;
  }
  hits.forEach((n) => {
    const frag = doc.createDocumentFragment();
    let at = 0;
    for (const m of n.nodeValue.matchAll(number)) {
      frag.append(n.nodeValue.slice(at, m.index));
      const b = doc.createElement('bdi');
      b.dir = 'ltr';
      b.textContent = m[0];
      frag.append(b);
      at = m.index + m[0].length;
    }
    frag.append(n.nodeValue.slice(at));
    n.replaceWith(frag);
  });
}

if (!design) {
  ready(() => {
    stickyOffset();
    tabbar();
    drawIn();
    isolatePhones();
  });
}
